import React, { useMemo, useState } from "react";
import Icons from "../icons";
import TestCasePicker from "./TestCasePicker";
import TicketXrayPicker from "./TicketXrayPicker";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* Wizard steps, in order (mirrors the report creation flow). */
const STEPS = [
  { id: "details", label: "Details", hint: "Name your execution" },
  { id: "select", label: "Test cases", hint: "Pick by ticket or test suite" },
  { id: "review", label: "Review", hint: "Confirm & create" },
];

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";

/* ------------------------------------------------------------------ */
/*  Stepper header                                                     */
/* ------------------------------------------------------------------ */
function Stepper({ current, onStepClick, maxReached }) {
  return (
    <ol className="flex items-center">
      {STEPS.map((step, idx) => {
        const isDone = idx < current;
        const isActive = idx === current;
        const clickable = idx <= maxReached;
        return (
          <li key={step.id} className="flex flex-1 items-center last:flex-none">
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick(idx)}
              className={[
                "flex items-center gap-3 text-left",
                clickable ? "cursor-pointer" : "cursor-not-allowed",
              ].join(" ")}
            >
              <span
                className={[
                  "flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-semibold ring-1 transition",
                  isActive
                    ? "bg-brand-600 text-white ring-brand-600"
                    : isDone
                    ? "bg-brand-50 text-brand-700 ring-brand-600/30"
                    : "bg-white text-slate-400 ring-slate-300",
                ].join(" ")}
              >
                {isDone ? <Icons.Check className="h-5 w-5" /> : idx + 1}
              </span>
              <span className="hidden sm:block">
                <span
                  className={[
                    "block text-sm font-semibold",
                    isActive || isDone ? "text-slate-900" : "text-slate-400",
                  ].join(" ")}
                >
                  {step.label}
                </span>
                <span className="block text-xs text-slate-400">{step.hint}</span>
              </span>
            </button>
            {idx < STEPS.length - 1 && (
              <span
                className={[
                  "mx-3 h-px flex-1",
                  idx < current ? "bg-brand-500" : "bg-slate-200",
                ].join(" ")}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* ================================================================== */
/*  Wizard                                                             */
/* ================================================================== */
export default function CreateExecutionWizard({
  boards = [],
  userkey,
  onCancel,
  onCreated,
  initialName = "",
  initialDescription = "",
  initialSelected = {},
  initialLinkTickets = [],
  initialStep = 0,
  // When launched from a ticket: restrict the picker to these keys and mirror
  // the cycle into Jira as an Xray Test Execution on create.
  scopeTicketKeys = null,
  createInXray = false,
  // Rendered inside a dialog that already has its own title bar and close
  // button, so the page header here would be duplicate chrome.
  embedded = false,
}) {
  const [step, setStep] = useState(initialStep);
  const [maxReached, setMaxReached] = useState(initialStep);

  // Step 1 — details
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  // Jira ticket anchors the caller already resolved (the Tickets-page path).
  // There is no "link a ticket" step: any other ticket is inferred from the tests
  // chosen in step 2, which already sit under their ticket.
  const [linkTickets] = useState(initialLinkTickets);

  // Step 2 — test cases (accumulated across boards -> mixed allowed)
  // testcasekey -> { name, board_id, boardName, priority }
  const [selected, setSelected] = useState(initialSelected);
  // How the user picks: "ticket" walks Jira ticket -> Xray test -> Playwright
  // case (the default, since an execution is really about a requirement);
  // "board" is the original flat browse-by-board list.
  const [pickMode, setPickMode] = useState("ticket");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const goTo = (idx) => {
    setStep(idx);
    setMaxReached((m) => Math.max(m, idx));
  };
  const next = () => goTo(Math.min(step + 1, STEPS.length - 1));
  const back = () => goTo(Math.max(step - 1, 0));

  const boardName = useMemo(
    () => (id) => boards.find((b) => b.id === id)?.name || id,
    [boards]
  );

  const isSelected = (k) => !!selected[k];

  // Toggle a set of cases as a unit (single case or a whole group). If all are
  // already selected we clear them; otherwise we add the lot.
  const onToggleMany = (caseObjs) =>
    setSelected((prev) => {
      const next = { ...prev };
      const allSel = caseObjs.length > 0 && caseObjs.every((c) => next[c.testcasekey]);
      caseObjs.forEach((c) => {
        if (allSel) {
          delete next[c.testcasekey];
        } else {
          next[c.testcasekey] = {
            name: c.name,
            board_id: c.board_id,
            // The ticket/Xray picker already knows the board's display name;
            // the board picker doesn't, so fall back to resolving it locally.
            boardName: c.board_name || boardName(c.board_id) || "Requirement Board",
            priority: c.priority,
          };
        }
      });
      return next;
    });

  const selectedKeys = Object.keys(selected);

  // Which Jira stories this execution covers, taken straight from the chosen
  // tests. This is what gets linked in Jira — no separate step, and it can't
  // disagree with what's actually being run.
  const selectedJiraKeys = useMemo(() => {
    const keys = new Set();
    selectedKeys.forEach((k) => {
      const tk = selected[k]?.ticket_key;
      if (tk) keys.add(tk);
    });
    return Array.from(keys);
  }, [selected, selectedKeys]);

  // For display: the pre-resolved anchors plus anything derived from selection.
  const allTicketLabels = useMemo(() => {
    const labels = new Set(linkTickets.map((t) => t.ticket_key));
    selectedJiraKeys.forEach((k) => labels.add(k));
    return Array.from(labels);
  }, [linkTickets, selectedJiraKeys]);

  // Group the selected cases by board for the review step.
  const groupedSelected = useMemo(() => {
    const groups = {};
    selectedKeys.forEach((k) => {
      const s = selected[k];
      (groups[s.boardName] = groups[s.boardName] || []).push({ key: k, ...s });
    });
    return groups;
  }, [selected, selectedKeys]);

  const canLeaveDetails = name.trim().length > 0;

  const submit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${API_BASE_URL}executions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          userkey: userkey || null,
          testcase_keys: selectedKeys,
          link_ticket_keys: linkTickets.map((t) => t.ticketkey),
          // Derived from the chosen tests; the API creates any missing anchor.
          link_jira_keys: selectedJiraKeys,
          create_in_xray: !!createInXray,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.data?.executionkey) {
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      // The execution exists either way; a failed Jira mirror is reported rather
      // than treated as a create failure, so the user doesn't lose the cycle.
      if (body.data.xray_error) {
        setSubmitError(
          `Created in Cortex, but the Jira execution wasn't: ${body.data.xray_error} ` +
            "— retry with “Create Executions in Jira” on the execution page.",
        );
        setSubmitting(false);
        return;
      }
      onCreated?.(body.data);
    } catch (e) {
      setSubmitError(e.message || "Could not create execution.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // Embedded in a dialog: fill the height so the step body can scroll and
    // the footer can sit at the bottom edge.
    <div className={embedded ? "flex min-h-0 flex-1 flex-col" : undefined}>
      {/* Header */}
      {!embedded && (
        <div className="mb-6 flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            <Icons.ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Create execution</h1>
            <p className="text-sm text-slate-500">
              Name the cycle, add test cases from any test suite, then create it.
            </p>
          </div>
        </div>
      )}

      {/* Stepper. Tighter when embedded — the hosting dialog already supplies
          its own padding, and doubling it up squeezed the picker. */}
      <div
        className={[
          "rounded-xl border border-slate-200 bg-card shadow-sm",
          embedded ? "mb-4 flex-none px-5 py-3.5" : "mb-6 p-5",
        ].join(" ")}
      >
        <Stepper current={step} onStepClick={goTo} maxReached={maxReached} />
      </div>

      {/* Step body */}
      <div
        className={[
          "rounded-xl border border-slate-200 bg-card shadow-sm",
          embedded
            ? "cq-scroll min-h-0 flex-1 overflow-y-auto p-4"
            : "p-6",
        ].join(" ")}
      >
        {/* ---- Step 1: Details ---- */}
        {step === 0 && (
          <div className="max-w-xl space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Execution name
              </label>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Release 1.2 regression cycle"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Description <span className="text-slate-400">(optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="What does this execution cover?"
                className={inputCls}
              />
            </div>
          </div>
        )}

        {/* ---- Step 2: Test cases ---- */}
        {step === 1 && (
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
              <span className="text-sm text-slate-500">
                <span className="font-semibold text-slate-800">{selectedKeys.length}</span>{" "}
                selected
              </span>
              <div className="flex items-center gap-2">
                {/* Two ways to pick: by requirement (default) or by board. Hidden
                    when the cycle is mirrored into Jira — a board-picked case
                    with no Xray test behind it couldn't be reported back. */}
                {!createInXray && (
                  <div className="inline-flex rounded-lg border border-slate-300 p-0.5">
                    {[
                      { id: "ticket", label: "By ticket / test" },
                      { id: "board", label: "By test suite" },
                    ].map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setPickMode(m.id)}
                        className={[
                          "rounded-md px-2.5 py-1 text-xs font-medium transition",
                          pickMode === m.id
                            ? "bg-brand-600 text-white"
                            : "text-slate-600 hover:bg-slate-50",
                        ].join(" ")}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                )}
                {selectedKeys.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelected({})}
                    className="text-sm font-medium text-brand-700 hover:text-brand-800"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
            {pickMode === "ticket" ? (
              <TicketXrayPicker
                isSelected={isSelected}
                onToggleMany={onToggleMany}
                scopeTicketKeys={scopeTicketKeys}
              />
            ) : (
              <TestCasePicker
                boards={boards}
                isSelected={isSelected}
                onToggleMany={onToggleMany}
              />
            )}
          </div>
        )}

        {/* ---- Step 3: Review ---- */}
        {step === 2 && (
          <div className="space-y-6">
            {createInXray && (
              <div className="rounded-xl border border-violet-200 bg-violet-50/70 px-4 py-3">
                <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-violet-700">
                  <span className="rounded-full bg-violet-200 px-1.5 py-0.5 text-[9px] tracking-normal text-violet-800">
                    Test
                  </span>
                  This also happens in Jira
                </p>
                <ol className="mt-1.5 space-y-1 text-[11px] leading-snug text-violet-900">
                  <li>
                    1. A <span className="font-semibold">Test Execution</span> issue is
                    created holding these {selectedKeys.length} test(s), every one at
                    TODO.
                  </li>
                  <li>
                    2. It is linked to{" "}
                    <span className="font-mono font-semibold">
                      {allTicketLabels.join(", ") || "the ticket"}
                    </span>{" "}
                    and added to any Test Plan tracking it.
                  </li>
                  <li>
                    3. When you run it, each step's{" "}
                    <span className="font-semibold">before/after screenshots</span> are
                    attached as evidence on the matching test step and the statuses
                    are pushed to the same issue.
                  </li>
                </ol>
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Execution name
                </p>
                <p className="mt-1 text-sm font-medium text-slate-800">{name}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Test cases
                </p>
                <p className="mt-1 text-sm font-medium text-slate-800">
                  {selectedKeys.length}
                </p>
              </div>
              {allTicketLabels.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Linked tickets
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-800">
                    {allTicketLabels.join(", ")}
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    From the tests you picked &mdash; linked in Jira automatically.
                  </p>
                </div>
              )}
              {description && (
                <div className="sm:col-span-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Description
                  </p>
                  <p className="mt-1 text-sm text-slate-600">{description}</p>
                </div>
              )}
            </div>

            {selectedKeys.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 px-6 py-8 text-center text-sm text-slate-500">
                No test cases selected. You can add them now, or later from the
                execution page.
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedSelected).map(([bName, items]) => (
                  <div key={bName}>
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      {bName} · {items.length}
                    </p>
                    <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
                      {items.map((it) => (
                        <li
                          key={it.key}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700"
                        >
                          <Icons.Check className="h-3.5 w-3.5 flex-none text-brand-500" />
                          <span className="truncate">{it.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {submitError && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {submitError}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer nav. Embedded, this is the dialog's footer: pinned to the
          bottom edge, full-bleed (negative margins cancel the host's padding)
          and separated by a divider. */}
      <div
        className={[
          "flex items-center justify-between",
          embedded
            ? "-mx-4 -mb-4 mt-4 flex-none border-t border-slate-200 bg-slate-50 px-5 py-3.5"
            : "mt-6",
        ].join(" ")}
      >
        <button
          type="button"
          onClick={step === 0 ? onCancel : back}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          {step === 0 ? "Cancel" : (<><Icons.ChevronLeft className="h-4 w-4" /> Back</>)}
        </button>

        {step < STEPS.length - 1 ? (
          <button
            type="button"
            onClick={next}
            disabled={step === 0 && !canLeaveDetails}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue
            <Icons.ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !name.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Icons.Spinner className="h-4 w-4 animate-spin" />
                Creating…
              </>
            ) : (
              <>
                <Icons.Check className="h-4 w-4" />
                Create execution
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
